import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { EditablePageShell } from "@/components/site/EditablePageShell";
import { pageQueryOptions } from "@/lib/pages.functions";
import { submitLead } from "@/lib/leads.functions";
import { getStoredUtm, track } from "@/lib/tracking";
import { toast } from "sonner";
import { SHARE_IMAGES } from "@/lib/share-images";

export const Route = createFileRoute("/events")({
  loader: ({ context }) => context.queryClient.ensureQueryData(pageQueryOptions("events")),
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.title || "Events & Bulk Orders — Glow Long Golf" },
      { name: "description", content: loaderData?.meta_description || "Bachelor parties, corporate outings, resorts, and night-golf tournaments. Tell us how many players." },
      { property: "og:title", content: loaderData?.title || "Events & Bulk Orders — Glow Long Golf" },
      { property: "og:description", content: loaderData?.meta_description || "Bachelor parties, corporate outings, resorts, and night-golf tournaments. Tell us how many players." },
      { property: "og:url", content: "https://glowlonggolf.com/events" },
      { property: "og:image", content: SHARE_IMAGES.backyardTrails },
      { name: "twitter:image", content: SHARE_IMAGES.backyardTrails },
    ],
    links: [{ rel: "canonical", href: "https://glowlonggolf.com/events" }],
  }),

  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive" role="alert">{error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-sm text-muted-foreground">Not found.</div>,
  component: EventsPage,
});

function EventsPage() {
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData(e.currentTarget);
    const data = Object.fromEntries(fd.entries()) as Record<string, string>;
    const res = await submitLead({
      data: {
        type: "bulk",
        email: data.email,
        phone: data.phone || undefined,
        name: data.name,
        source: "events_page",
        utm: getStoredUtm(),
        payload: {
          organization: data.organization,
          event_date: data.event_date,
          players: data.players,
          quantity: data.quantity,
          custom_packaging: data.custom_packaging === "on",
          delivery_deadline: data.delivery_deadline,
          notes: data.notes,
        },
      },
    });
    setLoading(false);
    if ((res as any).ok) { track("bulk_order_inquiry"); toast.success("Got it — we'll reply within 1 business day"); (e.target as HTMLFormElement).reset(); }
    else toast.error("Could not submit. Try again.");
  }

  return (
    <EditablePageShell slug="events" defaults={{ eyebrow: "Events & Bulk", title: "Events & bulk orders", lead: "Bachelor parties, corporate outings, resorts, charity scrambles, and night-golf tournaments. Tell us how many players." }}>
      <section className="mx-auto max-w-3xl px-4 pb-16 sm:px-6 lg:px-8">
        <form onSubmit={onSubmit} className="grid gap-4 rounded-3xl border border-border bg-card p-6 sm:grid-cols-2">
          <Field label="Your name" name="name" required />
          <Field label="Organization (optional)" name="organization" />
          <Field type="email" label="Email" name="email" required />
          <Field label="Phone (optional)" name="phone" />
          <Field type="date" label="Event date" name="event_date" />
          <Field type="number" label="# of players" name="players" min="2" />
          <Field type="number" label="Estimated # of balls" name="quantity" min="6" />
          <Field type="date" label="Delivery deadline" name="delivery_deadline" />
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" name="custom_packaging" /> Interested in custom packaging / co-branding
          </label>
          <label className="col-span-2 text-sm">
            <span className="mb-1 block font-medium">Notes</span>
            <textarea name="notes" rows={4} className="w-full rounded-lg border border-input bg-background p-3 text-sm" />
          </label>
          <button disabled={loading} className="col-span-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90 disabled:opacity-60">
            {loading ? "Sending…" : "Request a quote"}
          </button>
        </form>
      </section>
    </EditablePageShell>
  );
}

function Field({ label, name, type = "text", required, min }: { label: string; name: string; type?: string; required?: boolean; min?: string }) {
  return (
    <label className="text-sm">
      <span className="mb-1 block font-medium">{label}</span>
      <input type={type} name={name} required={required} min={min} className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm" />
    </label>
  );
}
